import { useAuth } from "./AuthContext";

// Mapeo de type_id a rol (debe coincidir con el backend)
const ROLES = {
  ADMIN: "admin",
  USER: "user",
};

const USER_TYPES = {
  ADMIN: 1,
  USER: 2,
  USER_PUBLIC: 3,
};

const SECTIONS = {
  DASHBOARD: "dashboard",
  PATHWAYS: "pathways",
  SCENARIOS: "scenarios",
  RESULTS: "results",
  COMPARISON: "comparison",
  AI_ASSISTANT: "ai_assistant",
};

// type_id -> role string
function mapTypeIdToRole(typeId) {
  if (Number(typeId) === USER_TYPES.ADMIN) return ROLES.ADMIN;
  if (Number(typeId) === USER_TYPES.USER) return ROLES.USER;
  if (Number(typeId) === USER_TYPES.USER_PUBLIC) return ROLES.USER;
  return ROLES.USER;
}

function normalizeTypeId(typeId) {
  if (Number(typeId) === USER_TYPES.ADMIN) return USER_TYPES.ADMIN;
  if (Number(typeId) === USER_TYPES.USER) return USER_TYPES.USER;
  if (Number(typeId) === USER_TYPES.USER_PUBLIC) return USER_TYPES.USER;
  return USER_TYPES.USER;
}

// Permisos por type_id
const TYPE_PERMISSIONS = {
  [USER_TYPES.ADMIN]: [
    SECTIONS.PATHWAYS,
    SECTIONS.SCENARIOS,
    SECTIONS.RESULTS,
    SECTIONS.COMPARISON,
    SECTIONS.AI_ASSISTANT,
  ],
  [USER_TYPES.USER]: [
    SECTIONS.PATHWAYS,
    SECTIONS.SCENARIOS,
    SECTIONS.RESULTS,
    SECTIONS.COMPARISON,
    SECTIONS.AI_ASSISTANT,
  ],
};

/**
 * Hook para acceder a funciones de roles y permisos
 * @returns {Object} Objeto con funciones y datos de rol
 */
export function useRole() {
  const { user } = useAuth();

  // Obtener el rol del usuario actual
  const getUserRole = () => {
    if (!user || !user.type_id) return ROLES.USER;
    return mapTypeIdToRole(user.type_id);
  };

  // Verificar si usuario tiene un rol específico (puede ser exact o admin)
  const hasRole = (requiredRole) => {
    if (!user || !user.type_id) return false;
    const userRole = getUserRole();
    // ADMIN siempre tiene acceso
    if (userRole === ROLES.ADMIN) return true;
    // Verificación exacta
    return userRole === requiredRole;
  };

  // Verificar si usuario tiene alguno de varios roles
  const hasAnyRole = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.some((role) => hasRole(role));
  };

  // Verificar si usuario tiene todos los roles
  const hasAllRoles = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.every((role) => hasRole(role));
  };

  // Verificar acceso a una sección
  const hasAccessToSection = (section) => {
    if (!user || !user.type_id) return false;
    const permissions = TYPE_PERMISSIONS[normalizeTypeId(user.type_id)] || [];
    return permissions.includes(section);
  };

  // Obtener todas las secciones a las que el usuario tiene acceso
  const getAccessibleSections = () => {
    if (!user || !user.type_id) return [];
    return TYPE_PERMISSIONS[normalizeTypeId(user.type_id)] || [];
  };

  // Verificar si es admin
  const isAdmin = () => getUserRole() === ROLES.ADMIN;

  // Verificar si es usuario regular
  const isUser = () => hasRole(ROLES.USER);

  return {
    // Funciones de verificación
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasAccessToSection,
    getUserRole,
    getAccessibleSections,
    // Funciones específicas por rol
    isAdmin,
    isUser,
    // Constantes
    ROLES,
    SECTIONS,
  };
}
